import dayjs from "dayjs"
import { Biobox, SubscriptionItem, TbrItem, TeamMember } from "../../../../types"
import { Autoclave, AutoclaveType, BioboxCompliance, BioboxFilters, FormattedBiobox, Frequency, SporetestingResult, SporetestLog, SporetestLogRow, StatusFilters, TestKit } from "../types"
import * as XLSX from 'xlsx'
import { updateKitSterilization } from "../../../../services/biologicalServices"
import { AutoclaveTypeId, SporetestLogAction } from "../enums"
import { getUserName } from "../../tasks/utils/helpers"

const getCompliance = (biobox: Biobox) => {
  const compliance: BioboxCompliance = {
    compliance_code: biobox.compliance_code,
    compliance_message_long: biobox.message_long,
    compliance_message_short: biobox.message_short,
    compliance_status: biobox.compliance_status,
  }

  return compliance
}

const getBiobox = (biobox: Biobox) => {
  const compliance = getCompliance(biobox)

  const box: FormattedBiobox = {
    box_id: biobox.box_id,
    box_number: biobox.box_number,
    box_size: biobox.box_size,
    active: biobox.active,
    activeStatus: biobox.active ? 'active' : 'inactive',
    exp_date: biobox.exp_date.slice(0, 10),
    item_number: biobox.item_number,
    lot_number: biobox.lot_number,
    tests_run: biobox.tests_run,
    purchase_date: biobox.purchase_date,
    city: biobox.city,
    doctor_name: biobox.doctor_name,
    customer_number: biobox.customer_number,
    name: biobox.name,
    state: biobox.state,
    created_date: biobox.created_date,
    updated_date: biobox.updated_date,
    autoclave_id: biobox.autoclave_id,
    box_type: biobox.box_type,
    compliance: [compliance],
  }

  return box
}

export const getFormattedBioboxes = (bioboxes: Biobox[]) => {
  const bioboxMap: Map<number, FormattedBiobox> = new Map()

  bioboxes.forEach(biobox => {
    const boxId = biobox.box_id

    if (bioboxMap.has(boxId)) {
      const compliance = getCompliance(biobox)
      bioboxMap.get(boxId)?.compliance.push(compliance)
    } else {
      const formattedBox = getBiobox(biobox)
      bioboxMap.set(boxId, formattedBox)
    }
  })

  return Array.from(bioboxMap, ([boxId, formattedBox]) => formattedBox)
}

export const getBioboxesByStatus = (bioboxes: FormattedBiobox[])  => {
  const boxesByStatus = new Map<BioboxFilters, FormattedBiobox[]>([
    [BioboxFilters.Active, []],
    [BioboxFilters.UpToDate, []],
    [BioboxFilters.AttentionNeeded, []],
    [BioboxFilters.Expired, []],
    [BioboxFilters.Inactive, []],
  ])

  bioboxes.forEach(box => {
    const complianceStatus = box.compliance[0].compliance_status
    const isBioboxEmpty = box.compliance[0].compliance_code === 'BIO_BOX_EMPTY'

    if (box.active && !isBioboxEmpty) {
      boxesByStatus.get(BioboxFilters.Active)?.push(box)
    }

    if (boxesByStatus.has(complianceStatus)) {
      boxesByStatus.get(complianceStatus)?.push(box)
    }
  })

  return boxesByStatus
}

export const getBioboxExport = (bioboxes: FormattedBiobox[]) => {
  const rows = bioboxes.map(box => {
    return {
      ['Status']: box.compliance[0].compliance_message_short,
      ['Box Number']: box.box_number,
      ['Lot Number']: box.lot_number,
      ['Practice']: box.name,
      ['Location']: `${box.city}, ${box.state}`,
      ['Tests Left']: `${box.box_size - box.tests_run} of ${box.box_size}`,
      ['Expiration Date']: dayjs(box.exp_date).format('MM/DD/YYYY'),
    }
  })

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, ws, 'Bio Boxes')
  XLSX.writeFile(wb, 'biobox_compliance_report.xlsx')
}

export const getFilteredBioboxes = (search: string, status: BioboxFilters, bioboxesByStatus: Map<BioboxFilters, FormattedBiobox[]>) => {
  const boxes = bioboxesByStatus.get(status) ?? []

  if (search) {
    return boxes.filter(box => {
      const cleanSearch = search.trim().toLowerCase()
      
      return box.box_number.includes(cleanSearch) 
        || box.lot_number.includes(cleanSearch)
    })
  } else return boxes
}

export const getTestsLeftClass = (boxSize: number, testsRun: number) => {
  let className = ''

  if (testsRun === boxSize) {
    className = 'badge badge-danger background-danger'
  } else if ((boxSize - testsRun) < 5) {
    className = 'badge badge-warning background-warning'
  }

  return className
}

export const getExpirationDateClass = (expirationDate: string) => {
  const today = new Date()
  const expDate = new Date(expirationDate)

  let className = ''

  if (today > expDate) {
    className = 'color-danger'
  } else if (new Date(today.setMonth(today.getMonth() + 1)) > expDate) {
    className = 'color-warning'
  }

  return className
}

export const getAutoShipmentStatus = (bioboxes: Biobox[], tbrEligibleItems: TbrItem[], subscriptions: SubscriptionItem[] ) => {
  let hasAutoShipment = false
  let hasEligibleItems = false

  if (bioboxes.length) {
    const hasBoxSubscriptions = subscriptions.find(subscription => {
      const subscriptionId = subscription.itemId.trim()
      const foundBox = bioboxes.find(box => box.item_number.trim() === subscriptionId)

      return Boolean(foundBox)
    })

    if (Boolean(hasBoxSubscriptions)) {
      hasAutoShipment = true
      hasEligibleItems = true
    } else if (tbrEligibleItems.length) {
      const hasBoxTbrItems = bioboxes.find(box => {
        const boxId = box.item_number.trim()
        const foundItem = tbrEligibleItems.find(item => item.ItemNumber.trim() === boxId)

        return Boolean(foundItem)
      })

      if (Boolean(hasBoxTbrItems)) {
        hasEligibleItems = true
      }
    }
  }

  return { hasAutoShipment, hasEligibleItems }
}

export const getHasUnassignedBoxes = (bioboxes: Biobox[]) => {
  const unassignedBox = bioboxes.find(box => {
    return box.active && box.box_type === 'mesa' && box.autoclave_id === null
  })

  return Boolean(unassignedBox)
}

export const getComplianceStatuses = (bioboxes: FormattedBiobox[]) => {
    const initialStatus = { 1: 0, 2: 0, 3: 0, 4: 0 }

    return bioboxes.reduce((acc: Record<number, number>, item) => {
      acc[item.compliance[0].compliance_status]++
      return acc
    }, initialStatus)
}

export const getStatusFilterOptions = () => {
  return Object.keys(StatusFilters).map(key => {
    const statusFilterKey = key as keyof typeof StatusFilters
    const bioboxKey = key as keyof typeof BioboxFilters

    return { label: StatusFilters[statusFilterKey], status: BioboxFilters[bioboxKey]}
  })  
}

export const getAutoclaveFrequencies = () => {
  const frequenciesByValue = new Map([
    [8, 'Daily'],
    [10, 'Weekly'],
    [13, 'Bi-Monthly'],
    [12, 'Monthly'],
  ])

  return frequenciesByValue
}

export const updateSterilizationMethod = async (customerNumber: string, autoclaveTypeId: string | number, boxNumber: string, boxTests: SporetestingResult[]) => {
  const autoclaveTypeById = new Map([
    [AutoclaveTypeId.Steam, 'S'],
    [AutoclaveTypeId.DriHeat, 'D'],
    [AutoclaveTypeId.Chemical, 'C'],
    [AutoclaveTypeId.eTo, 'D']
  ])
  
  const typeId = Number(autoclaveTypeId)
  const abbreviation = autoclaveTypeById.get(typeId)

  const hasUnusedTests = boxTests.some(test => {
    return test.test_result === null
  })

  if (abbreviation && hasUnusedTests) {
    const testKit: TestKit = {
      kitId: boxNumber,
      sterilizationMethod: abbreviation,
    }

    await updateKitSterilization(customerNumber, testKit)
  }
}

export const getAutoclaveTypeName = (autoclave: Partial<Autoclave> | null, autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>) => {
  const id = Number(autoclave?.autoclavetype_id)
  const type = autoclaveTypeById.get(id)
  const name = type?.autoclavetype_name ?? ''

  return name
}

export const getAutoclaveFrequency = (autoclave: Partial<Autoclave> | null, autoclaveFrequencyById: Map<number, Frequency>) => {
  const id = Number(autoclave?.autoclave_frequency)
  const frequency = autoclaveFrequencyById.get(id)
  const description = frequency?.frequency_description ?? ''

  return description
}

export const getSporetestLog = (customerNumber: string, action: SporetestLogAction, autoclaveId: number | null, formData: SporetestingResult) => {
  const log: SporetestLog = {
    customer_number: customerNumber,
    autoclave_id: autoclaveId && Number(autoclaveId),
    box_id: Number(formData.box_id),
    test_result_id: Number(formData.test_result_id),
    sample_date: formData.sample_date ?? '',
    test_operator: formData.test_operator ?? '',
    action,
  }
  
  return log
}

export const getSporetestLogRows = (sporetestLogs: SporetestLog[], bioboxesById: Map<number, Biobox>, sporetestsById: Map<number, SporetestingResult>, usersById: Map<number, TeamMember>) => {
  const rows = sporetestLogs.map((log, index) => {
    const box = bioboxesById.get(log.box_id)
    const sporetest = sporetestsById.get(log.test_result_id)
    const userId = Number(log.login_user_id!)

    const box_number = box?.box_number ?? ''
    const test_number = sporetest?.test_number ?? ''
    const sample_date = dayjs(log.sample_date).utc()
    const test_operator = log.test_operator 
    const created_at = log.created_at ? dayjs(log.created_at) : dayjs()
    const user_name = getUserName(userId, usersById)
    const action = log.action

    const row: SporetestLogRow = {
      id: index,
      box_number,
      test_number,
      sample_date,
      test_operator,
      created_at,
      user_name, 
      action,
    }

    return row
  })

  return rows
}
